import { JSX, MouseEvent, useCallback, useEffect, useState } from 'react';
import { Confirm, Title, useAuthenticated, useNotify, usePermissions } from 'react-admin';
import { Alert, Box, Button, Checkbox, Chip, LinearProgress, Stack, Tab, Tabs, Typography } from '@mui/material';
import { permissionManager } from '../permissions/manager';
import { eyebrow, numeric } from '../dashboard/components/styles/dashboard-tokens';
import {
    ActionResult,
    FailedMessage,
    FailedSection,
    QUEUES_CONTROL_PERMISSION,
    QueueDepth,
    QueuesReport,
    ScheduleHealth,
    TransportWorker,
    WorkBacklog,
    deleteFailedMessages,
    describeAge,
    describeWorker,
    loadFailedMessages,
    loadQueues,
    retryFailedMessages,
    setSchedulePaused,
} from './queuesApi';

/**
 * How often the screen refetches. The timer is the floor, not the mechanism: an action reloads
 * immediately, so this only catches what changed elsewhere.
 */
const POLL_MS: number = 30000;

type LoadState =
    | { status: 'loading' }
    | { status: 'failed', reason: string }
    | { status: 'loaded', report: QueuesReport };

type Confirming = 'retry' | 'delete' | null;

/**
 * Which section is on screen.
 *
 * The five sections were stacked and the page was a long scroll, so they are tabs as of 2026-09-21
 * (the user's call). **Waiting work leads**, because it is where nearly all the real waiting work
 * sits: `messenger_messages` is small in normal running, and a screen opening on "Queues" showed a
 * nearly empty table while thousands of activations could be sitting due one section below.
 */
type TabKey = 'waiting' | 'queues' | 'failed' | 'schedules' | 'workers';

/**
 * A total that is only a total when every figure in it was a reading.
 *
 * One unreadable count makes the sum an understatement, and an understated number presented as a
 * total is worse than no number: it reads as a measurement. Null here becomes an em dash on the
 * tab, the same treatment `Figure` gives a count it could not take.
 */
const sumOrNull = (values: (number | null)[]): number | null =>
    values.some((one): boolean => one === null)
        ? null
        : values.reduce((total: number, one): number => total + (one ?? 0), 0);

/**
 * A tab's name, with a badge when there is something behind it worth knowing about.
 *
 * **Tabs hide things, and that is the cost of this layout**: an admin reading Waiting work cannot
 * see that twelve messages have failed. The badge is what pays it back, so it appears only when it
 * would change what someone does - a count above zero, or a count that could not be taken. A row of
 * zeroes across five tabs would be noise, and noise is what stops a badge being read at all.
 */
const TabLabel = ({ title, count, tone }: {
    title: string,
    count: number | null,
    tone: 'default' | 'error',
}): JSX.Element => (
    <Stack direction="row" alignItems="center" spacing={ 1 }>
        <span>{ title }</span>
        { count === null && (
            <Chip size="small" variant="outlined" color="warning" label="—" sx={ { height: 18 } } />
        ) }
        { count !== null && count > 0 && (
            <Chip size="small" color={ tone } label={ count } sx={ { height: 18, ...numeric } } />
        ) }
    </Stack>
);

/**
 * Take focus off the button that opens a confirmation before the dialog opens. MUI hides the page
 * behind a dialog from assistive technology, and a button left focused underneath it is one a
 * screen reader user is still on while it is hidden (Chrome logs "Blocked aria-hidden").
 */
const blurTrigger = (event: MouseEvent<HTMLButtonElement>): void => event.currentTarget.blur();

const panel = {
    p: 2.5,
    borderRadius: 2,
    border: 1,
    borderColor: 'divider',
    bgcolor: 'background.paper',
} as const;

const Figure = ({ value, label }: { value: number | null, label: string }): JSX.Element => (
    <Stack spacing={ 0.25 } sx={ { minWidth: 96 } }>
        <Typography sx={ { ...numeric, fontSize: 22, lineHeight: 1.1 } }>
            { /* A count that could not be taken is not zero, and must not read as an empty queue. */ }
            { value === null ? '—' : value }
        </Typography>
        <Typography sx={ eyebrow }>{ label }</Typography>
    </Stack>
);

const Row = ({ children }: { children: JSX.Element | JSX.Element[] }): JSX.Element => (
    <Box sx={ { py: 1.5, borderTop: 1, borderColor: 'divider' } }>{ children }</Box>
);

const QueueRow = ({ queue }: { queue: QueueDepth }): JSX.Element => (
    <Row>
        <Stack direction="row" alignItems="center" spacing={ 3 } flexWrap="wrap" useFlexGap>
            <Typography sx={ { ...numeric, minWidth: 180, fontWeight: 600 } }>{ queue.queue }</Typography>
            <Figure value={ queue.waiting } label="Waiting" />
            <Figure value={ queue.delayed } label="Retry delayed" />
            <Figure value={ queue.inFlight } label="In flight" />
            <Typography variant="body2" color="text.secondary">
                { 'Oldest ' + describeAge(queue.oldestAgeSeconds) }
            </Typography>
        </Stack>
    </Row>
);

const WorkerRow = ({ worker }: { worker: TransportWorker }): JSX.Element => (
    <Row>
        <Stack direction="row" alignItems="center" spacing={ 2 } flexWrap="wrap" useFlexGap>
            <Typography sx={ { ...numeric, flex: 1, minWidth: 320 } }>{ worker.transport }</Typography>
            <Chip
                size="small"
                label={ describeWorker(worker) }
                color={ worker.consumed === true ? 'success' : 'default' }
                variant={ worker.consumed === true ? 'filled' : 'outlined' }
            />
        </Stack>
    </Row>
);

const ScheduleRow = ({ schedule, canControl, busy, onPause }: {
    schedule: ScheduleHealth,
    canControl: boolean,
    busy: boolean,
    onPause: (schedule: ScheduleHealth) => void,
}): JSX.Element => (
    <Row>
        <Stack direction="row" alignItems="center" spacing={ 2 } flexWrap="wrap" useFlexGap>
            <Stack spacing={ 0.25 } sx={ { flex: 1, minWidth: 280 } }>
                <Typography sx={ { ...numeric, fontWeight: 600 } }>{ schedule.name }</Typography>
                <Typography variant="body2" color="text.secondary">
                    { schedule.triggers.length === 0 ? 'No triggers set' : schedule.triggers.join(', ') }
                </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={ { minWidth: 110 } }>
                { 'Ran ' + describeAge(schedule.lastRunAgeSeconds) }
            </Typography>
            <Chip
                size="small"
                label={ describeWorker({
                    consumed: schedule.consumed,
                    lastSeenAgeSeconds: schedule.workerLastSeenAgeSeconds,
                }) }
                color={ schedule.consumed === true ? 'success' : 'default' }
                variant={ schedule.consumed === true ? 'filled' : 'outlined' }
            />
            { /* Two reasons, never one "paused". A window releases its own when it ends; an admin's
                 lasts until someone resumes it, so they are different problems with different
                 fixes and an admin needs to know which one they are looking at. */ }
            { schedule.pausedByMaintenance && <Chip size="small" color="warning" label="Held by maintenance" /> }
            { schedule.pausedByAdmin && <Chip size="small" color="error" label="Paused by an admin" /> }
            { canControl && (
                <Button
                    size="small"
                    disabled={ busy }
                    color={ schedule.pausedByAdmin ? 'primary' : 'inherit' }
                    onClick={ (): void => onPause(schedule) }
                >
                    { schedule.pausedByAdmin ? 'Resume' : 'Pause' }
                </Button>
            ) }
        </Stack>
    </Row>
);

const BacklogRow = ({ backlog }: { backlog: WorkBacklog }): JSX.Element => (
    <Row>
        <Stack direction="row" alignItems="center" spacing={ 2 } flexWrap="wrap" useFlexGap>
            <Stack spacing={ 0.25 } sx={ { flex: 1, minWidth: 300 } }>
                <Typography sx={ { fontWeight: 600 } }>{ backlog.title }</Typography>
                <Typography variant="body2" color="text.secondary" sx={ numeric }>
                    { 'Drained by ' + backlog.drainedBy }
                </Typography>
            </Stack>
            <Figure value={ backlog.count } label="Due now" />
        </Stack>
    </Row>
);

const FailedRow = ({ message, canControl, selected, onToggle }: {
    message: FailedMessage,
    canControl: boolean,
    selected: boolean,
    onToggle: (id: string) => void,
}): JSX.Element => (
    <Row>
        <Stack direction="row" alignItems="flex-start" spacing={ 1.5 }>
            { /* A message with no id cannot be acted on, so it offers no box rather than one that
                 would silently do nothing. */ }
            { canControl && message.id !== null && (
                <Checkbox
                    size="small"
                    checked={ selected }
                    onChange={ (): void => onToggle(message.id as string) }
                    inputProps={ { 'aria-label': 'Select ' + message.class } }
                    sx={ { mt: -0.5 } }
                />
            ) }
            <Stack spacing={ 0.25 } sx={ { flex: 1, minWidth: 0 } }>
                <Typography sx={ { ...numeric, fontWeight: 600, wordBreak: 'break-all' } }>
                    { message.class }
                </Typography>
                <Typography variant="body2" color="error.main" sx={ { wordBreak: 'break-word' } }>
                    { message.error ?? 'No reason was recorded' }
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={ numeric }>
                    { [
                        message.originalTransport === null ? 'origin unknown' : 'from ' + message.originalTransport,
                        message.attempts === null ? null : message.attempts + ' attempts',
                        message.failedAt === null ? null : 'failed ' + new Date(message.failedAt).toLocaleString(),
                    ].filter((one): boolean => one !== null).join('  ·  ') }
                </Typography>
            </Stack>
        </Stack>
    </Row>
);

const Section = ({ title, hint, action, children }: {
    title: string,
    hint: string,
    action?: JSX.Element,
    children: JSX.Element | JSX.Element[],
}): JSX.Element => (
    <Box sx={ panel }>
        <Stack direction="row" alignItems="flex-start" spacing={ 2 }>
            <Box sx={ { flex: 1 } }>
                <Typography variant="h6" component="h2">{ title }</Typography>
                <Typography variant="body2" color="text.secondary" sx={ { mb: 1 } }>{ hint }</Typography>
            </Box>
            { action }
        </Stack>
        { children }
    </Box>
);

/**
 * HQ-API's queues: what is waiting, what is sweeping, whether anything is consuming it, and what
 * has failed.
 *
 * Stages 1 to 4 of the HQ queue screens. Looking is `HQ_System::queues`; retrying, deleting and
 * pausing are `HQ_System::queues_control`, a sibling of it in the tree so it can be withheld from
 * an admin who can still open this screen. HQ-API enforces both whatever this renders.
 *
 * **Five sections because there are five different questions**, and the answer to one is misleading
 * without the others. A transport missing from "Queues" is empty, which is either healthy or nobody
 * consuming it, and only "Workers" tells those apart. A schedule with a stale last run is either
 * paused, unconsumed, or simply infrequent, and the row carries all three.
 *
 * **They are tabs rather than a stack as of 2026-09-21**, because five sections was a long scroll.
 * The risk that introduces is that a tab hides a problem, which the counts on the tab labels exist
 * to answer - see `TabLabel`. One poll still fills all five, so switching tabs costs nothing and
 * every section is as fresh as the last refresh whether it was on screen or not.
 */
export const HqApiQueuesPage = (): JSX.Element => {
    useAuthenticated();
    const notify = useNotify();
    const { permissions } = usePermissions();
    const canControl: boolean = permissionManager.isAllowed(permissions, QUEUES_CONTROL_PERMISSION);

    const [state, setState] = useState<LoadState>({ status: 'loading' });
    const [failed, setFailed] = useState<FailedSection | null>(null);
    const [tab, setTab] = useState<TabKey>('waiting');
    const [selected, setSelected] = useState<string[]>([]);
    const [confirming, setConfirming] = useState<Confirming>(null);
    const [busy, setBusy] = useState<boolean>(false);

    const load = useCallback(async (): Promise<void> => {
        try {
            const [report, failures] = await Promise.all([loadQueues(), loadFailedMessages()]);
            setState({ status: 'loaded', report });
            setFailed(failures);
            // Drop anything that is no longer there, so a stale tick cannot carry an id into the
            // next action after another admin has dealt with it.
            const present = failures.messages.map((one): string | null => one.id);
            setSelected((was): string[] => was.filter((id): boolean => present.includes(id)));
        } catch (error) {
            setState({
                status: 'failed',
                reason: error instanceof Error ? error.message : 'The queues could not be read',
            });
        }
    }, []);

    useEffect((): (() => void) => {
        void load();
        const timer: number = window.setInterval((): void => { void load(); }, POLL_MS);

        return (): void => window.clearInterval(timer);
    }, [load]);

    const toggle = (id: string): void =>
        setSelected((was): string[] => was.includes(id) ? was.filter((one): boolean => one !== id) : [...was, id]);

    /**
     * Reports what HQ-API did and, separately, what it would not do and why.
     *
     * **Refusals are notified first, and that ordering is load-bearing.** react-admin shows one
     * notification at a time and queues the rest, so whichever is sent first is the one an admin
     * actually reads. Driving this in the browser on 2026-09-21 showed the success toast sitting in
     * front of "this came from a schedule and cannot be re-queued", which is the half that needed
     * reading: the success speaks for itself when the row disappears.
     *
     * Each distinct reason once: fifty copies of the same sentence tell an admin nothing more than
     * one does, and every extra one pushes the rest further down the queue.
     */
    const announce = (result: ActionResult, did: string): void => {
        const refused = Object.values(result.refused);
        [...new Set(refused)].forEach((why): void => notify(why, { type: 'warning' }));
        if (result.done.length > 0) {
            notify(result.done.length + ' ' + did, { type: 'success' });
        }
        if (result.done.length === 0 && refused.length === 0) {
            notify('Nothing was changed', { type: 'info' });
        }
    };

    const act = async (action: () => Promise<ActionResult>, did: string): Promise<void> => {
        setBusy(true);
        try {
            announce(await action(), did);
            setSelected([]);
            await load();
        } catch (error) {
            notify(error instanceof Error ? error.message : 'That could not be done', { type: 'error' });
        } finally {
            setBusy(false);
            setConfirming(null);
        }
    };

    const pause = async (schedule: ScheduleHealth): Promise<void> => {
        setBusy(true);
        try {
            await setSchedulePaused(schedule.name, !schedule.pausedByAdmin);
            notify(
                schedule.name + (schedule.pausedByAdmin ? ' resumed' : ' paused'),
                { type: schedule.pausedByAdmin ? 'success' : 'warning' },
            );
            await load();
        } catch (error) {
            notify(error instanceof Error ? error.message : 'The schedule could not be changed', { type: 'error' });
        } finally {
            setBusy(false);
        }
    };

    const failedActions = canControl && selected.length > 0
        ? (
            <Stack direction="row" spacing={ 1 }>
                <Button
                    size="small"
                    variant="outlined"
                    disabled={ busy }
                    onClick={ (event: MouseEvent<HTMLButtonElement>): void => {
                        blurTrigger(event);
                        setConfirming('retry');
                    } }
                >
                    { 'Retry ' + selected.length }
                </Button>
                <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    disabled={ busy }
                    onClick={ (event: MouseEvent<HTMLButtonElement>): void => {
                        blurTrigger(event);
                        setConfirming('delete');
                    } }
                >
                    { 'Delete ' + selected.length }
                </Button>
            </Stack>
        )
        : undefined;

    return (
        <Box sx={ { p: 2, maxWidth: 1100 } }>
            <Title title="HQ-API Queues" />

            { state.status === 'loading' && <LinearProgress /> }

            { state.status === 'failed' && (
                <Alert
                    severity="error"
                    action={ <Button color="inherit" size="small" onClick={ (): void => { void load(); } }>{ 'Try again' }</Button> }
                >
                    { state.reason }
                </Alert>
            ) }

            { state.status === 'loaded' && (
                <Stack spacing={ 3 }>
                    { !canControl && (
                        <Alert severity="info">
                            { 'You can see the queues but not change them.' }
                        </Alert>
                    ) }

                    <Box sx={ { borderBottom: 1, borderColor: 'divider' } }>
                        <Tabs
                            value={ tab }
                            onChange={ (_event: unknown, next: TabKey): void => setTab(next) }
                            aria-label="Queue sections"
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            <Tab
                                value="waiting"
                                id="queues-tab-waiting"
                                aria-controls="queues-panel-waiting"
                                label={ <TabLabel
                                    title="Waiting work"
                                    count={ sumOrNull(state.report.backlogs.map((one): number | null => one.count)) }
                                    tone="default"
                                /> }
                            />
                            <Tab
                                value="queues"
                                id="queues-tab-queues"
                                aria-controls="queues-panel-queues"
                                label={ <TabLabel
                                    title="Queues"
                                    count={ state.report.transports.available
                                        ? sumOrNull(state.report.transports.queues.map((one): number => one.total))
                                        : null }
                                    tone="default"
                                /> }
                            />
                            <Tab
                                value="failed"
                                id="queues-tab-failed"
                                aria-controls="queues-panel-failed"
                                label={ <TabLabel
                                    title="Failed"
                                    // Red, and the only tab that is: a failed message is the one
                                    // thing here that will not clear itself.
                                    count={ failed === null || !failed.available ? null : failed.messages.length }
                                    tone="error"
                                /> }
                            />
                            <Tab
                                value="schedules"
                                id="queues-tab-schedules"
                                aria-controls="queues-panel-schedules"
                                label={ <TabLabel
                                    title="Schedules"
                                    // Only the paused ones are counted. A schedule that is simply
                                    // infrequent is not a number anyone needs on a tab.
                                    count={ state.report.schedules.filter(
                                        (one): boolean => one.pausedByAdmin || one.pausedByMaintenance
                                    ).length }
                                    tone="default"
                                /> }
                            />
                            <Tab
                                value="workers"
                                id="queues-tab-workers"
                                aria-controls="queues-panel-workers"
                                label={ <TabLabel
                                    title="Workers"
                                    // Transports nobody has reported consuming. Not proof that
                                    // nothing is, which is why it is not the error colour.
                                    count={ state.report.workers.filter(
                                        (one): boolean => one.consumed !== true
                                    ).length }
                                    tone="default"
                                /> }
                            />
                        </Tabs>
                    </Box>

                    <Box
                        role="tabpanel"
                        id={ 'queues-panel-' + tab }
                        aria-labelledby={ 'queues-tab-' + tab }
                    >
                    { tab === 'queues' && (
                    <Section
                        title="Queues"
                        hint="Messages in messenger_messages. A queue holding nothing does not appear here."
                    >
                        <>
                            { !state.report.transports.available && (
                                <Alert severity="warning" sx={ { mt: 1 } }>
                                    { state.report.transports.reason ?? 'The message table could not be read.' }
                                </Alert>
                            ) }
                            { state.report.transports.available && state.report.transports.queues.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={ { pt: 1 } }>
                                    { 'Every queue is empty.' }
                                </Typography>
                            ) }
                            { state.report.transports.queues.map((queue): JSX.Element => (
                                <QueueRow key={ queue.queue } queue={ queue } />
                            )) }
                        </>
                    </Section>
                    ) }

                    { tab === 'failed' && (
                    <Section
                        title="Failed"
                        hint="Messages that ran out of retries. Retrying puts one back on its own queue for a worker; it does not run it here. Deleting cannot be undone."
                        action={ failedActions }
                    >
                        <>
                            { failed !== null && !failed.available && (
                                <Alert severity="warning" sx={ { mt: 1 } }>
                                    { failed.reason ?? 'The failed messages could not be read.' }
                                </Alert>
                            ) }
                            { failed !== null && failed.available && failed.messages.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={ { pt: 1 } }>
                                    { 'Nothing has failed.' }
                                </Typography>
                            ) }
                            { (failed?.messages ?? []).map((message, index): JSX.Element => (
                                <FailedRow
                                    key={ message.id ?? 'no-id-' + index }
                                    message={ message }
                                    canControl={ canControl }
                                    selected={ message.id !== null && selected.includes(message.id) }
                                    onToggle={ toggle }
                                />
                            )) }
                        </>
                    </Section>
                    ) }

                    { tab === 'workers' && (
                    <Section
                        title="Workers"
                        hint="Every transport a worker could consume. No worker reporting means nobody has said so, which is not proof that nothing is consuming it."
                    >
                        <>
                            { state.report.workers.map((worker): JSX.Element => (
                                <WorkerRow key={ worker.transport } worker={ worker } />
                            )) }
                        </>
                    </Section>
                    ) }

                    { tab === 'schedules' && (
                    <Section
                        title="Schedules"
                        hint="The ticks that do the sweeping. A schedule holds no messages of its own, so the question is whether it is still firing. A pause lasts until someone resumes it."
                    >
                        <>
                            { state.report.schedules.map((schedule): JSX.Element => (
                                <ScheduleRow
                                    key={ schedule.name }
                                    schedule={ schedule }
                                    canControl={ canControl }
                                    busy={ busy }
                                    onPause={ (one): void => { void pause(one); } }
                                />
                            )) }
                        </>
                    </Section>
                    ) }

                    { tab === 'waiting' && (
                    <Section
                        title="Waiting work"
                        hint="Work due in ordinary tables. A figure that does not fall means its sweep is not running, not that the work is slow."
                    >
                        <>
                            { state.report.backlogs.map((backlog): JSX.Element => (
                                <BacklogRow key={ backlog.key } backlog={ backlog } />
                            )) }
                        </>
                    </Section>
                    ) }
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                        { 'Refreshes every ' + (POLL_MS / 1000) + ' seconds. Every tab is as fresh as the last refresh.' }
                    </Typography>
                </Stack>
            ) }

            <Confirm
                isOpen={ confirming === 'retry' }
                loading={ busy }
                title="Retry these messages?"
                content={ 'Each goes back on the queue it came from for a worker to pick up. ' + selected.length + ' selected.' }
                onConfirm={ (): void => { void act((): Promise<ActionResult> => retryFailedMessages(selected), 'put back on their queues'); } }
                onClose={ (): void => setConfirming(null) }
            />
            <Confirm
                isOpen={ confirming === 'delete' }
                loading={ busy }
                title="Delete these messages?"
                content={ 'This cannot be undone, and the failed queue is the only copy. ' + selected.length + ' selected.' }
                onConfirm={ (): void => { void act((): Promise<ActionResult> => deleteFailedMessages(selected), 'deleted'); } }
                onClose={ (): void => setConfirming(null) }
            />
        </Box>
    );
};
